import React, { useState, useRef, useEffect, useCallback } from 'react';
import BBALoader from '../components/BBALoader';
import { ChevronLeft, ChevronRight, Map, Target, Clock, Building2, CheckSquare, Phone,
  BarChart3, Grid, FileText, Shield, UserCheck,
  Zap, Star, CheckCircle2, Lock, PlayCircle, ArrowRight,
  MessageCircleQuestion, Paperclip, ClipboardList } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { useResponsive } from '../contexts/ResponsiveContext';
import roadmapTemplateService from '../services/roadmapTemplateService';
import clientRoadmapService from '../services/clientRoadmapService';
import { getPillarEmoji } from '../utils/portalEmojis';
import { resolvePortalPagePath } from '../data/portalPageOptions';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createTask, uploadTaskFile } from '../services/taskService';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import Stepper, { Step } from '../components/Stepper';
import { createQuestion } from '../services/firebase/questions';
import { getPillarQuestionAssigneeUser, getPillarScheduleLink } from '../services/pillarsService';
import { X, ChevronDown, Upload, ExternalLink, Search } from 'lucide-react';
import { getAllInternalUsers } from '../services/internalUserService';
import { filterPortalAssignable } from '../utils/portalAssignable';
import { getInternalUsers } from '../services/userService';
import { useStrategyCalls } from '../hooks/useStrategyCalls';
import { useStrategyCallBooking } from '../hooks/useStrategyCallBooking';
import { ensureCalendlyAssetsLoaded } from '../utils/calendlyWidget';
import PortalBackground from '../components/PortalBackground';

// Custom breathing animation CSS
const breathingAnimationStyle = `
  @keyframes breathing {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
      border-color: rgb(16, 185, 129);
    }
    50% {
      box-shadow: 0 0 0 20px rgba(16, 185, 129, 0);
      border-color: rgb(34, 197, 94);
    }
  }
  
  .breathing-green {
    animation: breathing 2s ease-in-out infinite;
  }
`;

const RoadmapItem = ({ item, isActive, index, totalItems, currentStep, onAskQuestion, onScheduleCall, onCreateTask, onTemplateComplete, onCallBooking, onGoToPreviousRoadmapStep }) => {
  const { darkMode } = useTheme();
  const { isOnboardingMode, hasUsedOnboardingKey, markStepComplete } = useOnboarding();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isCompleted = index < currentStep;
  const isInProgress = index === currentStep;
  const isUpcoming = index > currentStep;
  
  // For onboarding mode, always show as in progress
  const showAsInProgress = isOnboardingMode && hasUsedOnboardingKey && item.department === 'onboarding';

  // Get storage key for this item's task step
  const getTaskStepKey = () => {
    if (!currentUser || !item.id) return null;
    return `roadmap_task_step_${currentUser.uid}_${item.id}`;
  };

  // Load saved task step from localStorage
  const getSavedTaskStep = () => {
    const key = getTaskStepKey();
    if (!key) return 1;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const step = parseInt(saved, 10);
        // Ensure step is within valid range
        if (item.tasks && item.tasks.length > 0) {
          return Math.max(1, Math.min(step, item.tasks.length));
        }
      }
    } catch (error) {
      console.error('Error loading saved task step:', error);
    }
    return 1;
  };

  // Save task step to localStorage
  const saveTaskStep = (step) => {
    const key = getTaskStepKey();
    if (!key) return;
    try {
      localStorage.setItem(key, step.toString());
    } catch (error) {
      console.error('Error saving task step:', error);
    }
  };

  // Get initial step from saved value or default to 1
  const [currentTaskStep, setCurrentTaskStep] = useState(() => getSavedTaskStep());

  // Reload saved step when item changes or becomes active
  useEffect(() => {
    if (isInProgress || showAsInProgress) {
      const key = getTaskStepKey();
      if (key) {
        try {
          const saved = localStorage.getItem(key);
          if (saved) {
            const step = parseInt(saved, 10);
            // Ensure step is within valid range
            if (item.tasks && item.tasks.length > 0) {
              const validStep = Math.max(1, Math.min(step, item.tasks.length));
              setCurrentTaskStep(validStep);
            }
          }
        } catch (error) {
          console.error('Error loading saved task step:', error);
        }
      }
    }
  }, [item.id, item.tasks, isInProgress, showAsInProgress, currentUser]);

  // Removed automatic task creation - tasks are no longer created from roadmap templates

  // Sample completion dates for completed steps
  const completionDates = {
    0: "January 15, 2024",
    1: "February 28, 2024"
  };

  // Color scheme for steps - cycling through colors
  const stepColors = [
    { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-600 dark:text-red-400', light: 'bg-red-50 dark:bg-red-900/20' },
    { bg: 'bg-yellow-500', border: 'border-yellow-500', text: 'text-yellow-600 dark:text-yellow-400', light: 'bg-yellow-50 dark:bg-yellow-900/20' },
    { bg: 'bg-teal-500', border: 'border-teal-500', text: 'text-teal-600 dark:text-teal-400', light: 'bg-teal-50 dark:bg-teal-900/20' },
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600 dark:text-blue-400', light: 'bg-blue-50 dark:bg-blue-900/20' },
    { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-600 dark:text-green-400', light: 'bg-green-50 dark:bg-green-900/20' },
    { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-600 dark:text-purple-400', light: 'bg-purple-50 dark:bg-purple-900/20' },
    { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-600 dark:text-orange-400', light: 'bg-orange-50 dark:bg-orange-900/20' },
    { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-600 dark:text-pink-400', light: 'bg-pink-50 dark:bg-pink-900/20' }
  ];
  
  const colorScheme = stepColors[index % stepColors.length];
  
  const isCurrentStep = isInProgress || showAsInProgress;
  const nodeSize = isCurrentStep ? 'w-24 h-24' : isCompleted ? 'w-16 h-16' : 'w-14 h-14';
  const iconSize = isCurrentStep ? 'w-12 h-12' : 'w-7 h-7';

  // Get emoji based on department or status
  const getStepEmoji = () => {
    // Use emoji from template if available
    if (item && item.emoji) return item.emoji;
    
    if (isCompleted) return '✅';
    if (isInProgress || showAsInProgress) return '🚀';

    const dept = (item && item.department && typeof item.department === 'string')
      ? item.department.toLowerCase()
      : '';
    return getPillarEmoji(dept);
  };

  // Get department pill color
  const getDepartmentPillColor = (dept) => {
    const colorMap = {
      'sales': 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      'operations': 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      'finance': 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      'technology': 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      'marketing': 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      'hr': 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      'onboarding': 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
    };
    return colorMap[dept] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
  };

  // Get department display name
  const getDepartmentName = (dept) => {
    const nameMap = {
      'sales': 'Sales',
      'operations': 'Operations',
      'finance': 'Finance',
      'technology': 'Technology',
      'marketing': 'Marketing',
      'hr': 'HR',
      'onboarding': 'Onboarding',
      'general': 'General'
    };
    // If not in map, capitalize first letter
    if (nameMap[dept?.toLowerCase()]) {
      return nameMap[dept.toLowerCase()];
    }
    // Capitalize first letter of each word
    return dept ? dept.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : 'General';
  };

  return (
    <div className={`relative ${isCurrentStep ? 'my-2' : 'my-0'}`}>
      {/* Step Container */}
      <div className={`relative flex ${isCurrentStep ? 'flex-col items-center gap-2' : 'items-center gap-6'}`}>
        {/* Content Box - Much larger for current step */}
        <div
          className={`${isCurrentStep ? 'w-full max-w-4xl rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm' : 'flex-1'} transition-all duration-300 ${
            !isCurrentStep && (isCompleted
              ? 'rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20 shadow-sm'
              : 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm')
          }`}
        >
          {isCurrentStep ? (
            // Giant Stepper for current step with sidebar
            <div className="p-5 w-full flex gap-6">
              {/* Main content - 2/3 width */}
              <div className="w-2/3">
                {/* Header */}
                <div className="text-center mb-3">
                {item.department && (
                  <div className="mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getDepartmentPillColor(item.department)}`}>
                      {getDepartmentName(item.department)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl">{getStepEmoji()}</span>
                  <h3 className="font-bold text-2xl text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {item.description}
                </p>
              </div>

              {/* Stepper with Tasks */}
              {(isCompleted || isInProgress || showAsInProgress) && item.tasks && item.tasks.length > 0 ? (
                <div className="w-full max-w-3xl mx-auto">
                  <Stepper
                    initialStep={currentTaskStep}
                    onStepChange={(step) => {
                      console.log(`Step ${step} of ${item.tasks.length}`);
                      setCurrentTaskStep(step);
                      saveTaskStep(step);
                    }}
                    onFinalStepCompleted={async () => {
                      console.log("All tasks completed!");
                      // Save the final step (totalSteps + 1) or reset to last step
                      if (item.tasks && item.tasks.length > 0) {
                        const finalStep = item.tasks.length;
                        setCurrentTaskStep(finalStep);
                        saveTaskStep(finalStep);
                      }
                      // Call the completion handler to mark template as completed and move to next
                      if (onTemplateComplete) {
                        await onTemplateComplete(item, index);
                      }
                    }}
                    onFirstStepBack={index > 0 && onGoToPreviousRoadmapStep ? onGoToPreviousRoadmapStep : undefined}
                    firstStepBackButtonText="Previous step"
                    backButtonText="Previous"
                    nextButtonText="Next"
                    stepCircleContainerClassName="bg-transparent"
                    contentClassName="min-h-[200px]"
                    stepContainerClassName="p-4"
                  >
                    {item.tasks.map((task, taskIndex) => {
                      let taskLink = null;
                      if (task.title === 'Sign your NDA') {
                        taskLink = '/start/nda';
                      } else if (task.title === 'Complete your Intake') {
                        taskLink = '/start/existing-conditions';
                      } else if (task.title === 'Create your Roadmap') {
                        taskLink = '/portal/roadmap-builder';
                      }

                      return (
                        <Step key={taskIndex}>
                          <div className="py-3">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 text-center">
                              {task.title}
                            </h2>
                            {task.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
                                {task.description}
                              </p>
                            )}
                            {/* View Course, portal page, call booking, or external link */}
                            {(task.courseId || task.courseTitle || task.portalPage || task.callBookingDepartment || (task.externalLink && String(task.externalLink).trim())) && (
                              <div className="mt-3 space-y-2">
                                {task.courseId && (
                                  <Link
                                    to={`/portal/courses/${task.courseId}`}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                                  >
                                    View Course
                                    <ArrowRight className="w-4 h-4" />
                                  </Link>
                                )}
                                {task.courseTitle && !task.courseId && (
                                  <Link
                                    to="/portal/courses"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                                  >
                                    View Course
                                    <ArrowRight className="w-4 h-4" />
                                  </Link>
                                )}
                                {task.portalPage && (
                                  <Link
                                    to={resolvePortalPagePath(task.portalPage)}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                                  >
                                    View Page
                                    <ArrowRight className="w-4 h-4" />
                                  </Link>
                                )}
                                {task.callBookingDepartment && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onCallBooking &&
                                      onCallBooking(task.callBookingDepartment, item.title)
                                    }
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                                  >
                                    Book Call
                                    <ArrowRight className="w-4 h-4" />
                                  </button>
                                )}
                                {task.externalLink && String(task.externalLink).trim() && (
                                  <a
                                    href={/^https?:\/\//i.test(String(task.externalLink).trim()) ? String(task.externalLink).trim() : `https://${String(task.externalLink).trim()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold transition-colors"
                                  >
                                    Open link
                                    <ExternalLink className="w-4 h-4" aria-hidden />
                                  </a>
                                )}
                              </div>
                            )}
                            {taskLink && (
                              <div className="mt-4">
                                <Link
                                  to={taskLink}
                                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                                >
                                  Go to {task.title}
                                  <ArrowRight className="w-4 h-4" />
                                </Link>
                              </div>
                            )}
                          </div>
                        </Step>
                      );
                    })}
                  </Stepper>
                </div>
                ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 text-base">No tasks available for this step.</p>
                </div>
              )}
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="w-1/3 flex flex-col gap-3 h-full">
                <button
                  onClick={() => onAskQuestion && onAskQuestion(item.department)}
                  className="flex-1 p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center justify-center text-center"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-blue-50 dark:bg-blue-900/30 mb-2">
                    <span className="text-xl leading-none" aria-hidden="true">💬</span>
                  </div>
                  <div className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Ask a Question</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Get help with this step</div>
                </button>
                <button
                  onClick={() => onScheduleCall && onScheduleCall(item.department)}
                  className="flex-1 p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center justify-center text-center"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-emerald-50 dark:bg-emerald-900/30 mb-2">
                    <span className="text-xl leading-none" aria-hidden="true">📞</span>
                  </div>
                  <div className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Schedule a Call</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Book time with your advisor</div>
                </button>
                <button
                  onClick={() => onCreateTask && onCreateTask(item.department)}
                  className="flex-1 p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center justify-center text-center"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-purple-50 dark:bg-purple-900/30 mb-2">
                    <span className="text-xl leading-none" aria-hidden="true">➕</span>
                  </div>
                  <div className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Create a Task</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Add a custom task</div>
                </button>
              </div>
            </div>
          ) : (
            // Compact view for non-current steps
            <div className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold flex items-center gap-2 text-base text-gray-900 dark:text-white">
                    <span>{getStepEmoji()}</span>
                    <span className="truncate">{item.title}</span>
                  </h3>
                </div>
                {isCompleted && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 shrink-0">
                    <CheckCircle2 className="w-3 h-3" />
                    Complete
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Question Modal Component - from MyQuestions.js
const QuestionModal = ({ isOpen, onClose, department }) => {
  const [questionText, setQuestionText] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();

  // Load internal users when modal opens
  useEffect(() => {
    if (isOpen && internalUsers.length === 0) {
      const loadUsers = async () => {
        try {
          setModalLoading(true);
          let users = [];
          try {
            users = await getAllInternalUsers();
          } catch (error) {
            try {
              users = await getInternalUsers();
            } catch (fallbackError) {
              console.error('Error loading users:', fallbackError);
              users = [];
            }
          }
          setInternalUsers(users);
          setModalLoading(false);
        } catch (error) {
          console.error('Error loading users:', error);
          setInternalUsers([]);
          setModalLoading(false);
        }
      };
      loadUsers();
    }
  }, [isOpen, internalUsers.length]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size <= 10 * 1024 * 1024) {
        setFile(selectedFile);
        setFileError('');
      } else {
        setFileError('File must be less than 10MB');
        setFile(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!assigneeId) {
      alert('Please select someone to ask.');
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedUser = internalUsers.find(user => user.id === assigneeId);
      const questionData = {
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: assigneeId === 'anyone' ? 'anyone' : assigneeId,
        assigneeName: assigneeId === 'anyone' ? 'Anyone' : (selectedUser?.name || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim()),
        questionText: questionText.trim(),
        department: department,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User'
      };
      await createQuestion(questionData);
      setQuestionText('');
      setAssigneeId('');
      setFile(null);
      setFileError('');
      onClose();
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Failed to submit question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    onClose();
    setQuestionText('');
    setAssigneeId('');
    setFile(null);
    setFileError('');
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={closeModal}
            className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
              <MessageCircleQuestion className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ask a Question</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Our team typically responds within one business day.</p>
          </div>
        </div>
        <div className="p-5">
          {modalLoading ? (
            <div className="flex items-center justify-center py-8">
              <BBALoader size={32} />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Your Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="questionText"
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="5"
                  placeholder="Type your question here..."
                  required
                />
              </div>
              <div>
                <label htmlFor="assigneeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Ask <span className="text-red-500">*</span>
                </label>
                <select
                  id="assigneeId"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="" disabled>Select someone to ask...</option>
                  <option value="anyone">Anyone</option>
                  {filterPortalAssignable(internalUsers).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Attachment <span className="text-gray-400 font-normal">(optional, max 10MB)</span>
                </label>
                {file ? (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Paperclip className="w-4 h-4" />
                    Click to attach a file
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                    />
                  </label>
                )}
                {fileError && (
                  <p className="text-red-500 text-xs mt-1">{fileError}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Schedule Modal Component - Department Selection from PortalCalendar.js
const ScheduleModal = ({ isOpen, onClose, preSelectedDepartment }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const departmentCards = [
    { id: 'sales', title: 'Sales', emoji: getPillarEmoji('sales'), tile: 'bg-red-50 dark:bg-red-900/30' },
    { id: 'operations', title: 'Operations', emoji: getPillarEmoji('operations'), tile: 'bg-blue-50 dark:bg-blue-900/30' },
    { id: 'technology', title: 'Technology', emoji: getPillarEmoji('technology'), tile: 'bg-purple-50 dark:bg-purple-900/30' },
    { id: 'finance', title: 'Finance', emoji: getPillarEmoji('finance'), tile: 'bg-emerald-50 dark:bg-emerald-900/30' },
    { id: 'marketing', title: 'Marketing', emoji: getPillarEmoji('marketing'), tile: 'bg-orange-50 dark:bg-orange-900/30' },
    { id: 'hr', title: 'HR', emoji: getPillarEmoji('hr'), tile: 'bg-amber-50 dark:bg-amber-900/30' }
  ];

  const handleDepartmentSelect = (departmentId) => {
    onClose();
    // Navigate to calendar with department pre-selected
    navigate(`/portal/calendar?department=${departmentId}&action=addEvent`);
  };

  const handleCloseModal = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={handleCloseModal}
            className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
              <Phone className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Choose Department</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Pick the team you'd like to book time with.</p>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {departmentCards.map((department) => (
              <button
                key={department.id}
                type="button"
                onClick={() => handleDepartmentSelect(department.id)}
                className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center text-center"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mb-2 ${department.tile}`}>
                  <span className="text-xl leading-none" aria-hidden="true">{department.emoji}</span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {department.title}
                </h3>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// UserAvatar component for CreateTaskModal
const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const { darkMode } = useTheme();
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (user.profileImage) {
    return (
      <img
        src={user.profileImage}
        alt={user.name}
        className={`${sizeClass} rounded-full object-cover ${className}`}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-semibold ${className} ${
      darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'
    }`}>
      {user.initials}
    </div>
  );
};

// Create Task Modal Component - from PortalTasks.js
const CreateTaskModal = ({ isOpen, onClose, department }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [newTask, setNewTask] = useState({
    title: '',
    assignedTo: [],
    description: '',
    files: []
  });
  const [internalUsers, setInternalUsers] = useState([]);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [error, setError] = useState(null);
  const assigneeDropdownRef = useRef(null);

  // Load internal users when modal opens
  useEffect(() => {
    if (isOpen && internalUsers.length === 0) {
      const loadUsers = async () => {
        try {
          const users = await getInternalUsers();
          setInternalUsers(users);
        } catch (error) {
          console.error('Error loading users:', error);
          setInternalUsers([]);
        }
      };
      loadUsers();
    }
  }, [isOpen, internalUsers.length]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setShowAssigneeDropdown(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleCreateTask = async () => {
    if (!currentUser) return;
    
    try {
      if (!newTask.title.trim()) {
        setError('Task title is required');
        return;
      }

      let clientAccountId = 'portal';
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accountId) {
            clientAccountId = userData.accountId;
          }
        }
      } catch (error) {
        console.error('Error getting client account ID:', error);
      }

      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        assignees: newTask.assignedTo.filter(id => id),
        accountId: clientAccountId,
        department: department,
        completed: false,
        status: 'Not Done',
        files: [],
        createdBy: currentUser.uid
      };

      const createdTask = await createTask(taskData);
      
      // Upload files if any
      if (newTask.files && newTask.files.length > 0) {
        for (const file of newTask.files) {
          try {
            await uploadTaskFile(createdTask.id, file);
          } catch (fileError) {
            console.error('Error uploading file:', fileError);
          }
        }
      }

      setNewTask({ title: '', assignedTo: [], description: '', files: [] });
      setError(null);
      onClose();
      alert('Task created successfully!');
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task. Please try again.');
    }
  };

  const closeModal = () => {
    setNewTask({ title: '', assignedTo: [], description: '', files: [] });
    setError(null);
    setShowAssigneeDropdown(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
          <button
            type="button"
            className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={closeModal}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-2">
              <ClipboardList className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Task</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Add a custom task for this step.</p>
          </div>
        </div>
        <div className="p-5">
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Task Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Enter task title"
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Description
            </label>
            <textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              placeholder="Enter task description"
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Assigned To
            </label>
            <div className="relative" ref={assigneeDropdownRef}>
              <button
                type="button"
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <div className="flex items-center gap-2">
                  {newTask.assignedTo.length > 0 ? (
                    newTask.assignedTo.map(userId => {
                      const user = internalUsers.find(u => u.id === userId);
                      return user ? (
                        <UserAvatar 
                          key={userId}
                          user={{
                            name: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName 
                              ? user.firstName
                              : user.email?.split('@')[0] || 'Unknown',
                            initials: user.firstName && user.lastName
                              ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                              : user.firstName
                              ? user.firstName.substring(0, 2).toUpperCase()
                              : (user.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                            profileImage: user.profileImage
                          }} 
                          size="sm" 
                        />
                      ) : null;
                    })
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">
                      Select assignees...
                    </span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAssigneeDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showAssigneeDropdown && (
                <div className="absolute z-10 w-full mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {filterPortalAssignable(internalUsers).map(user => (
                      <label
                        key={user.id}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={newTask.assignedTo.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTask(prev => ({
                                ...prev,
                                assignedTo: [...prev.assignedTo, user.id]
                              }));
                            } else {
                              setNewTask(prev => ({
                                ...prev,
                                assignedTo: prev.assignedTo.filter(id => id !== user.id)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                        />
                        <UserAvatar 
                          user={{
                            name: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName 
                              ? user.firstName
                              : user.email?.split('@')[0] || 'Unknown',
                            initials: user.firstName && user.lastName
                              ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                              : user.firstName
                              ? user.firstName.substring(0, 2).toUpperCase()
                              : (user.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                            profileImage: user.profileImage
                          }} 
                          size="sm" 
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.firstName
                            ? user.firstName
                            : user.email?.split('@')[0] || 'Unknown'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Attachments
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <Upload className="w-4 h-4" />
                <span>Choose files to attach</span>
                <input 
                  type="file" 
                  multiple 
                  className="hidden" 
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setNewTask(prev => ({
                      ...prev,
                      files: [...prev.files, ...newFiles]
                    }));
                  }}
                />
              </label>
              {newTask.files && newTask.files.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Selected files ({newTask.files.length}):
                  </div>
                  {newTask.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        onClick={() => setNewTask(prev => ({
                          ...prev,
                          files: prev.files.filter((_, i) => i !== index)
                        }))}
                        className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors"
            onClick={handleCreateTask}
          >
            Create task
          </button>
        </div>
      </div>
    </div>
  );
};

// Department Card View Component
const DepartmentCardView = ({ roadmapItems, loading, activeIndex, onCardSelect, isFiltered }) => {
  const { isMobile, isTablet } = useResponsive();

  if (loading) {
    return (
      <div className={isMobile ? 'p-2' : isTablet ? 'p-3' : 'p-4'}>
        <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'} ${isMobile ? 'gap-2' : 'gap-4'}`}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800"></div>
              <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
              <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getStatusIcon = (status, isActiveItem = false) => {
    if (isActiveItem || status === 'in-progress') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          <PlayCircle className="w-3 h-3" />
          In Progress
        </span>
      );
    }
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-3 h-3" />
            Complete
          </span>
        );
      case 'locked':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            <Lock className="w-3 h-3" />
            Upcoming
          </span>
        );
    }
  };

  const getStatusColors = (status, isActiveItem) => {
    // In progress (including the active Current Focus item) → yellow card
    if (isActiveItem || status === 'in-progress' || status === 'in_progress' || status === 'active') {
      return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20';
    }

    switch (status) {
      case 'completed':
        // Complete → green card
        return 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20';
      case 'locked':
      case 'not-started':
      default:
        // Upcoming → light gray card
        return 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50';
    }
  };

  // Get emoji based on department or item emoji
  const getDepartmentEmoji = (item) => {
    // Handle edge cases
    if (!item) return '📋';

    // Use emoji from item if available
    if (item.emoji) return item.emoji;

    // Handle if item is a string (backward compatibility)
    if (typeof item === 'string') {
      return getPillarEmoji(item);
    }

    const dept = (item.department && typeof item.department === 'string')
      ? item.department.toLowerCase()
      : '';
    return getPillarEmoji(dept);
  };

  // Get department pill color based on department
  const getDepartmentPillColor = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const colorMap = {
      'sales': 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      'operations': 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      'finance': 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      'technology': 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      'marketing': 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      'hr': 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      'onboarding': 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
      'general': 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
    };
    return colorMap[deptLower] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
  };

  // Get department name
  const getDepartmentName = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const nameMap = {
      'sales': 'Sales',
      'operations': 'Operations',
      'finance': 'Finance',
      'technology': 'Technology',
      'marketing': 'Marketing',
      'hr': 'HR',
      'onboarding': 'Onboarding',
      'general': 'General'
    };
    return nameMap[deptLower] || dept || 'General';
  };

  if (!roadmapItems || roadmapItems.length === 0) {
    return (
      <div className="p-4">
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
          <Map className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {isFiltered ? 'No matching roadmap items' : 'No roadmap items found'}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
            {isFiltered
              ? 'Try adjusting your search or department filter.'
              : "Your roadmap will appear here once it's set up."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={isMobile ? 'p-2' : isTablet ? 'p-3' : 'p-4'}>
      <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'} ${isMobile ? 'gap-2' : 'gap-4'}`}>
        {roadmapItems.map((item, index) => {
          // Filtered lists carry the item's index in the full roadmap so
          // highlighting and card selection still map to the right step
          const originalIndex = item.originalIndex ?? index;
          // Check if this is the active item from Current Focus tab
          const isActiveItem = activeIndex !== null && activeIndex !== undefined && originalIndex === activeIndex;
          // Override status to in-progress if this is the active item
          const displayStatus = isActiveItem ? 'in-progress' : item.status;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onCardSelect?.(originalIndex)}
              className={`w-full rounded-xl border shadow-sm p-4 transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${getStatusColors(item.status, isActiveItem)}`}
              aria-label={`Open ${item.title} in Current Focus`}
            >
              <div className="text-center flex flex-col h-full relative">
                <div className="mb-3 mt-1">
                  <span className="text-4xl">{getDepartmentEmoji(item)}</span>
                </div>
                <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-4">
                  {item.title}
                </h3>
                <div className="mt-auto flex items-center justify-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getDepartmentPillColor(item.department)}`}>
                    {getDepartmentName(item.department)}
                  </span>
                  {getStatusIcon(displayStatus, isActiveItem)}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const StepView = ({
  activeIndex,
  setActiveIndex,
  roadmapItems,
  setRoadmapItems,
  allRoadmapItems,
  setAllRoadmapItems,
  roadmapRef,
  itemsRef,
  forcedRoadmapStepIndex,
  onForcedRoadmapStepConsumed
}) => {
  const { darkMode } = useTheme();
  const { isOnboardingMode, hasUsedOnboardingKey, markStepComplete } = useOnboarding();
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    ensureCalendlyAssetsLoaded();
  }, []);

  // Load user profile once for Calendly prefill
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!currentUser?.uid) return;
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!cancelled && snap.exists()) setUserProfile(snap.data());
      } catch (e) {
        console.error('Error loading user profile for call booking:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  const { atLimit: strategyCallsAtLimit } = useStrategyCalls();

  const { openBooking, meetingAgendaModal } = useStrategyCallBooking({
    currentUser,
    userProfile,
    disabled: strategyCallsAtLimit,
    variant: 'roadmap',
    requireFocus: true,
  });

  const handleCallBooking = async (dept, focusTitle) => {
    if (strategyCallsAtLimit || !dept) return;

    const defaultCalendlyLinks = {
      sales: 'https://calendly.com/ken-zwz1/sales-strategy-call',
      operations: 'https://calendly.com/daryl-yxbw/operations-strategy-call?hide_gdpr_banner=1',
      technology: 'https://calendly.com/ianr-lhxe/technology-strategy-call?hide_gdpr_banner=1',
      finance: 'https://calendly.com/ianh-fm8t/finance-strategy-call?hide_gdpr_banner=1',
      marketing: 'https://calendly.com/parker-reignmediagroup',
    };

    let baseUrl = defaultCalendlyLinks[dept] || defaultCalendlyLinks.sales;
    try {
      if (currentUser?.uid) {
        const fromSettings = await getPillarScheduleLink(currentUser.uid, dept);
        if (fromSettings) baseUrl = fromSettings;
      }
    } catch (e) {
      console.error('Error loading pillar schedule link for call booking:', e);
    }

    openBooking({ url: baseUrl, initialFocus: focusTitle || '' });
  };
  
  const resolveInProgressIndex = (list) => {
    if (!list || list.length === 0) {
      return isOnboardingMode && hasUsedOnboardingKey ? 0 : 0;
    }
    const inProgressIndex = list.findIndex(item =>
      item.status === 'in-progress' || item.status === 'in_progress' || item.status === 'active'
    );
    return inProgressIndex !== -1 ? inProgressIndex : 0;
  };

  const [currentStep, setCurrentStep] = useState(() => {
    if (
      typeof forcedRoadmapStepIndex === 'number' &&
      forcedRoadmapStepIndex >= 0 &&
      roadmapItems?.length > forcedRoadmapStepIndex
    ) {
      return forcedRoadmapStepIndex;
    }
    return resolveInProgressIndex(roadmapItems);
  });

  const lastSyncedRoadmapRef = useRef(null);

  // When roadmap data loads or updates, sync to the in-progress tile. Skip if the list
  // reference is unchanged (e.g. after clearing forced step from an overview jump).
  useEffect(() => {
    if (!roadmapItems || roadmapItems.length === 0) return;

    if (typeof forcedRoadmapStepIndex === 'number' && forcedRoadmapStepIndex >= 0) {
      if (forcedRoadmapStepIndex < roadmapItems.length) {
        setCurrentStep(forcedRoadmapStepIndex);
        setActiveIndex(forcedRoadmapStepIndex);
        lastSyncedRoadmapRef.current = roadmapItems;
        onForcedRoadmapStepConsumed?.();
        return;
      }
      onForcedRoadmapStepConsumed?.();
    }

    if (lastSyncedRoadmapRef.current === roadmapItems) {
      return;
    }

    lastSyncedRoadmapRef.current = roadmapItems;

    const inProgressIndex = roadmapItems.findIndex(item =>
      item.status === 'in-progress' || item.status === 'in_progress' || item.status === 'active'
    );
    if (inProgressIndex !== -1) {
      setCurrentStep(inProgressIndex);
      setActiveIndex(inProgressIndex);
    } else if (activeIndex !== undefined && activeIndex !== null && activeIndex < roadmapItems.length) {
      setCurrentStep(activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only follow Firestore-driven list updates; activeIndex read for no-in-progress fallback
  }, [roadmapItems, forcedRoadmapStepIndex, onForcedRoadmapStepConsumed, setActiveIndex]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);

  const scrollToItem = (index) => {
    if (index < 0 || index >= roadmapItems.length) return;
    
    const itemRef = itemsRef.current[index];
    if (itemRef && roadmapRef.current) {
      const containerRect = roadmapRef.current.getBoundingClientRect();
      const itemRect = itemRef.getBoundingClientRect();
      
      roadmapRef.current.scrollTo({
        top: itemRef.offsetTop - containerRect.height / 2 + itemRect.height / 2,
        behavior: 'smooth'
      });
      
      setActiveIndex(index);
    }
  };

  const goToPreviousRoadmapStep = () => {
    if (currentStep <= 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    setActiveIndex(prev);
  };

  // Center the current step on mount and when activeIndex or roadmapItems changes
  useEffect(() => {
    scrollToItem(activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, roadmapItems]);
  
  // Handlers for modals
  const handleAskQuestion = (department) => {
    setCurrentDepartment(department);
    setShowQuestionModal(true);
  };

  const handleScheduleCall = (department) => {
    setCurrentDepartment(department);
    setShowScheduleModal(true);
  };

  const handleCreateTask = (department) => {
    setCurrentDepartment(department);
    setShowCreateTaskModal(true);
  };

  // Handle template completion - mark current as completed and next as in-progress
  const handleTemplateComplete = async (completedItem, completedIndex) => {
    if (!currentUser) return;

    try {
      // Get user's accountId
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        console.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const accountId = userData.accountId;

      if (!accountId) {
        console.error('No accountId found for user');
        return;
      }

      // Update current template to completed
      const templateId = completedItem.clientRoadmapId || completedItem.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(accountId, templateId, {
        status: 'completed',
        dateCompleted: new Date().toISOString()
      });

      // Find next template and update to in-progress
      const nextIndex = completedIndex + 1;
      if (nextIndex < roadmapItems.length) {
        const nextItem = roadmapItems[nextIndex];
        const nextTemplateId = nextItem.clientRoadmapId || nextItem.id;
        await clientRoadmapService.updateTemplateInClientRoadmap(accountId, nextTemplateId, {
          status: 'in-progress',
          dateStarted: new Date().toISOString()
        });

        // Reload roadmap data from Firestore to ensure we have the latest status
        const updatedRoadmap = await clientRoadmapService.getClientRoadmap(accountId);
        const updatedTransformedItems = updatedRoadmap.map((template) => {
          let status = 'locked';
          if (template.status === 'completed' || template.status === 'done') {
            status = 'completed';
          } else if (template.status === 'in-progress' || template.status === 'in_progress' || template.status === 'active') {
            status = 'in-progress';
          } else if (template.status === 'not-started' || template.status === 'not_started' || template.status === 'locked') {
            status = 'locked';
          }

          return {
            id: template.clientRoadmapId || template.id || `template-${Date.now()}-${Math.random()}`,
            title: template.title || 'Untitled',
            description: template.description || '',
            goal: template.goal || '',
            department: (template.department || 'general').toLowerCase(),
            status: status,
            tasks: template.tasks || [],
            navigators: template.navigators || [],
            emoji: template.emoji || null,
            order: template.order,
            allOrder: template.allOrder
          };
        });

        updatedTransformedItems.sort((a, b) => {
          const orderA = a.order || a.allOrder || 0;
          const orderB = b.order || b.allOrder || 0;
          return orderA - orderB;
        });

        // Find the new in-progress item index
        const newInProgressIndex = updatedTransformedItems.findIndex(item => item.status === 'in-progress');
        
        // Update both cached and displayed items
        setAllRoadmapItems(updatedTransformedItems);
        setRoadmapItems(updatedTransformedItems);

        // Move to next item (the one that's now in-progress)
        if (newInProgressIndex !== -1) {
          setActiveIndex(newInProgressIndex);
          setCurrentStep(newInProgressIndex);
        } else if (nextIndex < updatedTransformedItems.length) {
          setActiveIndex(nextIndex);
          setCurrentStep(nextIndex);
        }
      } else {
        // No next item, just update current
        const updatedItems = [...roadmapItems];
        updatedItems[completedIndex] = { ...completedItem, status: 'completed' };
        setAllRoadmapItems(updatedItems);
        setRoadmapItems(updatedItems);
      }
    } catch (error) {
      console.error('Error completing template:', error);
    }
  };


  const reversedItems = [...roadmapItems].reverse();
  
  // Color scheme for path segments
  const stepColors = [
    { path: 'bg-red-500', completed: 'bg-green-500' },
    { path: 'bg-yellow-500', completed: 'bg-green-500' },
    { path: 'bg-teal-500', completed: 'bg-green-500' },
    { path: 'bg-blue-500', completed: 'bg-green-500' },
    { path: 'bg-green-500', completed: 'bg-green-500' },
    { path: 'bg-purple-500', completed: 'bg-green-500' },
    { path: 'bg-orange-500', completed: 'bg-green-500' },
    { path: 'bg-pink-500', completed: 'bg-green-500' }
  ];

  return (
    <div className={`flex ${isOnboardingMode && hasUsedOnboardingKey ? 'h-auto' : 'h-auto'} w-full mx-auto relative`}>
      <div className="flex flex-row flex-grow bg-transparent rounded-lg items-center">
        <div 
          ref={roadmapRef} 
          className={`flex-grow overflow-y-auto px-4 py-2 scrollbar-hide relative ${
            isOnboardingMode && hasUsedOnboardingKey ? 'flex items-center justify-center' : ''
          }`}
          style={{ scrollbarWidth: 'none' }}
        >
          <div className={`${isOnboardingMode && hasUsedOnboardingKey ? 'w-full max-w-4xl' : 'max-w-4xl'} mx-auto relative`}>
            {roadmapItems
              .filter((item, index) => index === currentStep)
              .map((item, index) => {
                const originalIndex = currentStep;
                return (
                  <div 
                    key={originalIndex} 
                    ref={el => itemsRef.current[originalIndex] = el}
                    className="relative"
                  >
                    <RoadmapItem 
                      item={item}
                      isActive={originalIndex === activeIndex} 
                      index={originalIndex}
                      totalItems={roadmapItems.length}
                      currentStep={currentStep}
                      onAskQuestion={handleAskQuestion}
                      onScheduleCall={handleScheduleCall}
                      onCreateTask={handleCreateTask}
                      onTemplateComplete={handleTemplateComplete}
                      onCallBooking={handleCallBooking}
                      onGoToPreviousRoadmapStep={goToPreviousRoadmapStep}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Modals */}
      <QuestionModal
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        department={currentDepartment}
      />
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        preSelectedDepartment={currentDepartment}
      />
      <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        department={currentDepartment}
      />
      {meetingAgendaModal}
    </div>
  );
};


const Roadmap = () => {
  const { darkMode } = useTheme();
  const { isOnboardingMode, hasUsedOnboardingKey } = useOnboarding();
  const { currentUser } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const [activeIndex, setActiveIndex] = useState(0);
  const [forcedRoadmapStepIndex, setForcedRoadmapStepIndex] = useState(null);
  const [department, setDepartment] = useState('all');
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewDepartment, setOverviewDepartment] = useState('all');
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [allRoadmapItems, setAllRoadmapItems] = useState([]); // Cache all items
  const [loadingRoadmap, setLoadingRoadmap] = useState(true);
  const roadmapRef = useRef(null);
  const itemsRef = useRef([]);

  const handleForcedRoadmapStepConsumed = useCallback(() => {
    setForcedRoadmapStepIndex(null);
  }, []);

  const handleOverviewCardSelect = useCallback((index) => {
    setForcedRoadmapStepIndex(index);
    setActiveIndex(index);
    setDepartment('all');
  }, []);

  // Department options for the Overview filter, derived from the loaded roadmap
  const overviewDepartmentLabels = {
    'sales': 'Sales',
    'operations': 'Operations',
    'finance': 'Finance',
    'technology': 'Technology',
    'marketing': 'Marketing',
    'hr': 'HR',
    'onboarding': 'Onboarding',
    'general': 'General'
  };
  const getOverviewDepartmentLabel = (dept) =>
    overviewDepartmentLabels[dept] ||
    dept.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  const overviewDepartmentOptions = Array.from(
    new Set(roadmapItems.map(item => (item.department || 'general').toLowerCase()))
  ).sort((a, b) => getOverviewDepartmentLabel(a).localeCompare(getOverviewDepartmentLabel(b)));

  // Overview tab: filter cards by search text and department, keeping each
  // item's index in the full roadmap so card clicks jump to the right step
  const overviewSearchQuery = overviewSearch.trim().toLowerCase();
  const overviewFilteredItems = roadmapItems
    .map((item, index) => ({ ...item, originalIndex: index }))
    .filter(item =>
      overviewDepartment === 'all' ||
      (item.department || 'general').toLowerCase() === overviewDepartment
    )
    .filter(item =>
      !overviewSearchQuery ||
      [item.title, item.description, item.goal].some(field =>
        (field || '').toLowerCase().includes(overviewSearchQuery)
      )
    );
  const overviewIsFiltered = Boolean(overviewSearchQuery) || overviewDepartment !== 'all';

  // Overview data - combined items from all departments
  const overviewData = [
    { id: 'overview-1', title: 'CRM Implementation', description: 'Set up and configure customer relationship management system', status: 'completed' },
    { id: 'overview-2', title: 'Sales Process Optimization', description: 'Streamline sales workflows and improve conversion rates', status: 'in-progress' },
    { id: 'overview-3', title: 'Process Documentation', description: 'Document all key operational processes and procedures', status: 'completed' },
    { id: 'overview-4', title: 'Workflow Automation', description: 'Automate repetitive operational tasks and workflows', status: 'in-progress' },
    { id: 'overview-5', title: 'Financial Systems Setup', description: 'Configure accounting and financial management software', status: 'completed' },
    { id: 'overview-6', title: 'Budget Planning', description: 'Develop comprehensive budget planning and forecasting', status: 'in-progress' },
    { id: 'overview-7', title: 'Infrastructure Setup', description: 'Set up cloud infrastructure and hosting environment', status: 'completed' },
    { id: 'overview-8', title: 'Security Implementation', description: 'Implement security measures and protocols', status: 'in-progress' },
    { id: 'overview-9', title: 'Brand Identity', description: 'Develop and establish brand identity and guidelines', status: 'completed' },
    { id: 'overview-10', title: 'Content Strategy', description: 'Create comprehensive content marketing strategy', status: 'in-progress' },
    { id: 'overview-11', title: 'HRIS Implementation', description: 'Set up Human Resources Information System', status: 'completed' },
    { id: 'overview-12', title: 'Recruitment Process', description: 'Streamline recruitment and hiring processes', status: 'in-progress' },
    { id: 'overview-13', title: 'Lead Generation System', description: 'Implement automated lead generation and nurturing', status: 'locked' },
    { id: 'overview-14', title: 'Supply Chain Optimization', description: 'Improve supply chain efficiency and reduce costs', status: 'locked' },
    { id: 'overview-15', title: 'Financial Reporting', description: 'Create automated financial reporting dashboards', status: 'locked' },
    { id: 'overview-16', title: 'API Integration', description: 'Integrate third-party APIs and services', status: 'locked' },
    { id: 'overview-17', title: 'Social Media Campaign', description: 'Launch and manage social media campaigns', status: 'locked' },
    { id: 'overview-18', title: 'Employee Onboarding', description: 'Develop comprehensive employee onboarding program', status: 'locked' }
  ];

  // Onboarding roadmap items - simplified for onboarding mode
  const onboardingRoadmapItems = [
    {
      id: 'onboarding-1',
      title: 'Onboarding',
      description: 'Complete your onboarding process to unlock full access to the portal and begin your journey with Builders Breakthrough Advisors.',
      goal: 'Successfully complete all onboarding requirements',
      department: 'onboarding',
      tasks: [
        {
          id: 'task-1',
          title: 'Sign your NDA',
          description: 'Review and sign the Non-Disclosure Agreement to protect sensitive information',
          estimatedHours: 0,
          estimatedMinutes: 5,
          completed: false
        },
        {
          id: 'task-2',
          title: 'Complete your Intake',
          description: 'Fill out your business profile and share your current challenges and goals',
          estimatedHours: 0,
          estimatedMinutes: 60,
          completed: false
        },
        {
          id: 'task-3',
          title: 'Create your Roadmap',
          description: 'Work with our team to create your personalized business roadmap',
          estimatedHours: 1,
          estimatedMinutes: 0,
          completed: false
        }
      ],
      navigators: [
        { id: 1, name: 'Onboarding Specialist', role: 'Guide' }
      ]
    }
  ];

  // Load roadmap items from client roadmap data
  useEffect(() => {
    const loadClientRoadmap = async () => {
      if (isOnboardingMode && hasUsedOnboardingKey) {
        setRoadmapItems(onboardingRoadmapItems);
        return;
      }

      if (!currentUser) {
        setRoadmapItems([]);
        return;
      }

      try {
        setLoadingRoadmap(true);
        // Get user's accountId
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) {
          console.error('User document not found');
          setRoadmapItems([]);
          setLoadingRoadmap(false);
          return;
        }

        const userData = userDoc.data();
        const accountId = userData.accountId;

        if (!accountId) {
          console.error('No accountId found for user');
          setRoadmapItems([]);
          setLoadingRoadmap(false);
          return;
        }

        // Fetch client roadmap data
        const clientRoadmap = await clientRoadmapService.getClientRoadmap(accountId);
        
        if (!clientRoadmap || !Array.isArray(clientRoadmap)) {
          setRoadmapItems([]);
          setLoadingRoadmap(false);
          return;
        }

        // Transform roadmap templates to match component structure
        // Exclude templates that are marked as hidden (internal only)
        const transformedItems = clientRoadmap
          .filter((template) => template.status !== 'hidden' && template.status !== 'Hidden')
          .map((template) => {
          // Map status values
          let status = 'locked';
          if (template.status === 'completed' || template.status === 'done') {
            status = 'completed';
          } else if (template.status === 'in-progress' || template.status === 'in_progress' || template.status === 'active') {
            status = 'in-progress';
          } else if (template.status === 'not-started' || template.status === 'not_started' || template.status === 'locked') {
            status = 'locked';
          }

          return {
            id: template.clientRoadmapId || template.id || `template-${Date.now()}-${Math.random()}`,
            title: template.title || 'Untitled',
            description: template.description || '',
            goal: template.goal || '',
            department: (template.department || 'general').toLowerCase(),
            status: status,
            tasks: template.tasks || [],
            navigators: template.navigators || [],
            emoji: template.emoji || null,
            order: template.order,
            allOrder: template.allOrder
          };
        });

        // Sort by order if available
        transformedItems.sort((a, b) => {
          const orderA = a.order || a.allOrder || 0;
          const orderB = b.order || b.allOrder || 0;
          return orderA - orderB;
        });

        // Cache all items
        setAllRoadmapItems(transformedItems);
        
        // Find the in-progress item and set it as active
        const inProgressIndex = transformedItems.findIndex(item => 
          item.status === 'in-progress' || item.status === 'in_progress' || item.status === 'active'
        );
        
        // Set items based on current department
        if (department === 'all' || department === 'overview') {
          setRoadmapItems(transformedItems);
          
          // Set activeIndex to in-progress item if found, otherwise keep current or default to 0
          if (inProgressIndex !== -1) {
            setActiveIndex(inProgressIndex);
          }
        } else {
          setRoadmapItems([]);
        }
      } catch (error) {
        console.error('Error loading client roadmap:', error);
        setRoadmapItems([]);
      } finally {
        setLoadingRoadmap(false);
      }
    };

    loadClientRoadmap();
  }, [isOnboardingMode, hasUsedOnboardingKey, currentUser]); // Remove department from deps

  // Update displayed items when department changes (using cached data)
  useEffect(() => {
    if (isOnboardingMode && hasUsedOnboardingKey) {
      // Onboarding mode handled in main useEffect
      return;
    }

    if (department === 'all' || department === 'overview') {
      // Use cached items for instant switching
      setRoadmapItems(allRoadmapItems);
    } else {
      setRoadmapItems([]);
    }
  }, [department, allRoadmapItems, isOnboardingMode, hasUsedOnboardingKey]);
  
  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, roadmapItems.length);
    
    const handleScroll = () => {
      if (!roadmapRef.current) return;
      
      const containerRect = roadmapRef.current.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      itemsRef.current.forEach((itemRef, index) => {
        if (!itemRef) return;
        
        const itemRect = itemRef.getBoundingClientRect();
        const itemCenter = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(containerCenter - itemCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      setActiveIndex(closestIndex);
    };
    
    const roadmapContainer = roadmapRef.current;
    if (roadmapContainer) {
      roadmapContainer.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (roadmapContainer) {
        roadmapContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [roadmapItems]);
  
  return (
    <div className="relative min-h-screen p-4">
      <PortalBackground />
      <div className="relative">
      <style>{breathingAnimationStyle}</style>
      <div className="max-w-7xl mx-auto">
        <div className="bg-transparent rounded-lg p-2">
          {/* Centered Title */}
          <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
              <BackButton onClick={() => window.history.back()} />
            </div>
            <div className={`text-center ${isMobile ? 'px-14' : 'px-32'} py-1`}>
              <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Roadmap</h1>
            </div>
          </div>

          {/* Tabs - Hide in onboarding mode */}
          {!isOnboardingMode && (
            <div className="flex items-center justify-center gap-2 mb-4" aria-label="Tabs">
              <button
                onClick={() => setDepartment('all')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  department === 'all'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Target className="w-3.5 h-3.5" />
                Current Focus
              </button>
              <button
                onClick={() => setDepartment('overview')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  department === 'overview'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Grid className="w-3.5 h-3.5" />
                Overview
              </button>
            </div>
          )}

          {/* Overview search + department filter */}
          {!isOnboardingMode && department === 'overview' && (
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center justify-center gap-2 mb-4 px-4`}>
              <div className={`relative w-full ${isMobile ? '' : 'max-w-md'}`}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={overviewSearch}
                  onChange={(e) => setOverviewSearch(e.target.value)}
                  placeholder="Search your roadmap..."
                  className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                {overviewSearch && (
                  <button
                    type="button"
                    onClick={() => setOverviewSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <select
                value={overviewDepartment}
                onChange={(e) => setOverviewDepartment(e.target.value)}
                className={`${isMobile ? 'w-full' : 'w-48'} px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                aria-label="Filter by department"
              >
                <option value="all">All Departments</option>
                {overviewDepartmentOptions.map(dept => (
                  <option key={dept} value={dept}>
                    {getOverviewDepartmentLabel(dept)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bg-transparent rounded-lg p-0">
            {department === 'all' ? (
              <StepView
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                roadmapItems={roadmapItems}
                setRoadmapItems={setRoadmapItems}
                allRoadmapItems={allRoadmapItems}
                setAllRoadmapItems={setAllRoadmapItems}
                roadmapRef={roadmapRef}
                itemsRef={itemsRef}
                forcedRoadmapStepIndex={forcedRoadmapStepIndex}
                onForcedRoadmapStepConsumed={handleForcedRoadmapStepConsumed}
              />
            ) : (
              <DepartmentCardView
                roadmapItems={overviewFilteredItems}
                loading={loadingRoadmap}
                activeIndex={activeIndex}
                onCardSelect={handleOverviewCardSelect}
                isFiltered={overviewIsFiltered}
              />
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Roadmap; 